function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import ReactDOM from 'react-dom';
import React, { useState, createContext, useEffect, useCallback, useMemo, useReducer, useContext, useRef } from 'react';
import { Modal, Fade } from '@material-ui/core';
import cn from 'classnames';
import styles from "./GlobalContext.module.scss";
import { Toast } from "../Toast";
import { Loader } from "../Loader";

const useIsMounted = () => {
  const ref = useRef(true);
  useEffect(() => {
    return () => {
      ref.current = false;
    };
  }, []);
  return useCallback(() => ref.current, []);
};

function throwUnsetContextError(f) {
  return () => {
    throw new Error("Somebody is trying to use ".concat(f, " without a properly set context"));
  };
}

export const Context = /*#__PURE__*/createContext({
  getOpenedModals: throwUnsetContextError('getOpenedModals'),
  showModal: throwUnsetContextError('showModal'),
  hideModal: throwUnsetContextError('hideModal'),
  hideAllModals: throwUnsetContextError('hideAllModals'),
  startLoader: throwUnsetContextError('startLoader'),
  stopLoader: throwUnsetContextError('stopLoader'),
  showToast: throwUnsetContextError('showToast'),
  showStandardToast: throwUnsetContextError('showStandardToast'),
  showErrorToast: throwUnsetContextError('showErrorToast'),
  showSuccessToast: throwUnsetContextError('showSuccessToast'),
  hideToast: throwUnsetContextError('hideToast'),
  togglePrintMode: throwUnsetContextError('togglePrintMode'),
  isTestMode: false
});
const modalPromiseResolvers = [];
const initialState = {
  loaders: {},
  modals: [],
  toasts: [],
  toastTimers: {},
  isPrintMode: false
};
const modalTransitionDuration = 225;

function reducer(state, action) {
  switch (action.type) {
    case 'showModal':
      {
        const {
          content,
          closeOnClickOutside,
          closeOnEscapeKey,
          shouldBlurBackground,
          transparentBackdrop,
          disableEnforceFocus,
          resolve,
          id
        } = action;
        return _objectSpread(_objectSpread({}, state), {}, {
          modals: state.modals.concat({
            id,
            content,
            closeOnClickOutside,
            closeOnEscapeKey,
            shouldBlurBackground,
            resolve,
            transparentBackdrop,
            disableEnforceFocus
          })
        });
      }

    case 'dismissModal':
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          modals: state.modals.filter(m => m.id !== action.id)
        });
      }

    case 'startLoader':
      {
        const {
          name
        } = action;
        return _objectSpread(_objectSpread({}, state), {}, {
          loaders: _objectSpread(_objectSpread({}, state.loaders), {}, {
            [name]: ((state === null || state === void 0 ? void 0 : state.loaders[name]) || 0) + 1
          })
        });
      }

    case 'stopLoader':
      {
        const {
          name
        } = action;
        return _objectSpread(_objectSpread({}, state), {}, {
          loaders: _objectSpread(_objectSpread({}, state.loaders), {}, {
            [name]: Math.max(0, ((state === null || state === void 0 ? void 0 : state.loaders[name]) || 0) - 1)
          })
        });
      }

    case 'showToast':
      {
        const {
          content,
          id,
          duration,
          timer
        } = action;

        if (id && state.toasts.find(toast => toast.id === id)) {
          console.log("There is already a toast with the id ".concat(id));
          return state;
        } else {
          return _objectSpread(_objectSpread({}, state), {}, {
            toasts: state.toasts.concat({
              content: content,
              id: id,
              duration: duration,
              timer: timer
            })
          });
        }
      }

    case 'hideToast':
      {
        const {
          id
        } = action;
        return _objectSpread(_objectSpread({}, state), {}, {
          toasts: state.toasts.filter(toast => toast.id !== id)
        });
      }

    case 'setToastTimer':
      {
        return state;
      }

    case 'clearTimers':
      {
        state.toasts.map(toast => toast.timer).forEach(timer => clearTimeout(timer));
        return state;
      }

    case 'togglePrintMode':
      {
        const {
          isPrintMode
        } = state;
        return _objectSpread(_objectSpread({}, state), {}, {
          isPrintMode: !isPrintMode
        });
      }

    default:
      {
        throw new Error();
      }
  }
}

export function GlobalContextProvider(props) {
  const {
    children,
    ModalWrapper,
    isTestMode = false,
    isPrintMode = false,
    disableEnforceFocus
  } = props;
  const isMounted = useIsMounted();
  const [state, dispatch] = useReducer(reducer, _objectSpread(_objectSpread({}, initialState), {}, {
    isPrintMode
  }));
  const stateRef = useRef(state);
  stateRef.current = state;
  const somethingIsLoading = Object.values(state.loaders).some(count => count > 0);
  const shouldBlurBackground = state.modals.some(modal => modal.shouldBlurBackground);
  useEffect(() => {
    return () => {
      dispatch({
        type: 'clearTimers'
      });
    };
  }, []);
  const contextValue = useMemo(() => {
    const safeDispatch = params => isMounted() && dispatch(params);

    function showToast(params) {
      const defaultDuration = 5;
      const content = params;
      const id = "toast".concat(new Date().getTime());
      const duration = "".concat(defaultDuration * 0.95, "s");
      const timer = setTimeout(() => {
        safeDispatch({
          type: 'hideToast',
          id
        });
      }, defaultDuration * 1000);
      safeDispatch({
        type: 'showToast',
        content,
        id,
        duration,
        timer
      });
    }

    return {
      isTestMode,

      getOpenedModals() {
        return stateRef.current.modals;
      },

      showModal(params) {
        return new Promise(resolve => {
          safeDispatch(_objectSpread(_objectSpread({}, params), {}, {
            type: 'showModal',
            id: Date.now(),
            resolve
          }));
          modalPromiseResolvers.push(resolve);
        });
      },

      hideModal(value) {
        var _stateRef$current$mod, _stateRef$current$mod2;

        (_stateRef$current$mod = stateRef.current.modals[stateRef.current.modals.length - 1]) === null || _stateRef$current$mod === void 0 ? void 0 : (_stateRef$current$mod2 = _stateRef$current$mod.dismissModal) === null || _stateRef$current$mod2 === void 0 ? void 0 : _stateRef$current$mod2.call(_stateRef$current$mod, value);
      },

      hideAllModals(value) {
        for (const modal of stateRef.current.modals) {
          var _modal$dismissModal;

          (_modal$dismissModal = modal.dismissModal) === null || _modal$dismissModal === void 0 ? void 0 : _modal$dismissModal.call(modal, value);
        }
      },

      startLoader: name => safeDispatch({
        type: 'startLoader',
        name: name
      }),
      stopLoader: name => safeDispatch({
        type: 'stopLoader',
        name: name
      }),
      showToast,
      showStandardToast: props => showToast( /*#__PURE__*/React.createElement(Toast, props)),
      showErrorToast: (text, testid) => showToast( /*#__PURE__*/React.createElement(Toast, {
        type: "error",
        text: text,
        testid: testid
      })),
      showSuccessToast: (text, testid) => showToast( /*#__PURE__*/React.createElement(Toast, {
        type: "success",
        text: text,
        testid: testid
      })),
      hideToast: id => safeDispatch({
        type: 'hideToast',
        id
      }),
      togglePrintMode: () => safeDispatch({
        type: 'togglePrintMode'
      })
    };
  }, [isTestMode]);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.globalContext, {
      [styles.blur]: shouldBlurBackground,
      [styles.globalPrintMode]: state.isPrintMode
    })
  }, children), state.modals.map((modal, idx) => /*#__PURE__*/React.createElement(ModalContainer, {
    key: idx,
    modal: modal,
    ModalWrapper: ModalWrapper,
    dispatch: dispatch,
    isTestMode: isTestMode,
    disableEnforceFocus: disableEnforceFocus
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.toastsContainer
  }, state.toasts.map((toast, idx) => /*#__PURE__*/React.createElement("div", {
    key: toast.id || "idx".concat(idx),
    className: styles.toast,
    style: {
      '--toastDuration': toast.duration
    }
  }, toast.content))), somethingIsLoading && /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", {
    className: styles.globalLoader,
    "data-testid": "global-loader"
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "async-backdrop",
    className: styles.overlay
  }), /*#__PURE__*/React.createElement(Loader, null)), document.body));
}

function ModalContainer(_ref) {
  let {
    modal,
    ModalWrapper,
    isTestMode,
    disableEnforceFocus,
    dispatch
  } = _ref;
  const [open, setOpen] = useState(true);
  const [returnValue, setReturnValue] = useState(undefined);
  const canDismissModal = useRef();

  const registerCanDismiss = cb => canDismissModal.current = cb;

  const BackdropComponent = useMemo(() => getModalCustomBackdrop(isTestMode), [isTestMode]);

  const dismissModal = async function dismissModal(value) {
    let {
      force
    } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    if (canDismissModal.current && !force) {
      const confirmed = await canDismissModal.current();
      if (!confirmed) return;
    } // Remove resolve function from modalPromiseResolvers.


    const idx = modalPromiseResolvers.findIndex(r => r === modal.resolve);
    modalPromiseResolvers.splice(idx, 1);
    setReturnValue(value);
    setOpen(false);
  };

  modal.dismissModal = dismissModal;
  const modalContent = modal.content && (typeof modal.content === 'function' ? modal.content({
    dismissModal,
    registerCanDismiss
  }) : /*#__PURE__*/React.cloneElement(modal.content, {
    dismissModal,
    registerCanDismiss
  }));
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    disableEnforceFocus: disableEnforceFocus || modal.disableEnforceFocus,
    closeAfterTransition: true,
    BackdropComponent: BackdropComponent,
    BackdropProps: {
      modal
    },
    onClose: (e, reason) => {
      if (reason === 'escapeKeyDown' && modal.closeOnEscapeKey) {
        dismissModal();
      }
    }
  }, /*#__PURE__*/React.createElement(Fade, {
    timeout: isTestMode ? 0 : modalTransitionDuration,
    in: open,
    onExited: () => {
      dispatch({
        type: 'dismissModal',
        id: modal.id
      });
      modal.resolve(returnValue);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.modalContainer,
    "data-testid": "modal-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.modalBackdrop,
    "data-testid": "modal-backdrop",
    onClick: () => {
      if (modal.closeOnClickOutside) {
        dismissModal();
      }
    }
  }), ModalWrapper ? /*#__PURE__*/React.createElement(ModalWrapper, {
    dismissModal: dismissModal
  }, modalContent) : modalContent)));
}

const getModalCustomBackdrop = isTestMode => {
  return _ref2 => {
    let {
      open,
      onClick,
      modal
    } = _ref2;
    const FadeComp = Fade;
    return /*#__PURE__*/React.createElement(FadeComp, {
      timeout: isTestMode ? 0 : modalTransitionDuration,
      in: open,
      onClick: onClick
    }, /*#__PURE__*/React.createElement("div", {
      className: cn(styles.modalBackdrop, {
        [styles.modalBg]: !modal.transparentBackdrop
      })
    }));
  };
};

export function useGlobalContext() {
  return useContext(Context);
}